import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { useAuth } from '../../context/AuthContext';
import { RefreshCw, Users } from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';
const DEMO_BRAND_ID = 'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4';

const DATE_OPTIONS = [
  { value: 'last7', label: 'Last 7 days' },
  { value: 'last30', label: 'Last 30 days' },
  { value: 'last90', label: 'Last 90 days' },
  { value: 'last6', label: 'Last 6 months' },
  { value: 'last12', label: 'Last 12 months' },
  { value: 'thisMonth', label: 'This month' },
  { value: 'lastMonth', label: 'Last month' },
  { value: 'custom', label: 'Custom range' }
];

const formatNumber = (value) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return '0';
  return number.toLocaleString('en-US');
};

const formatCurrency = (value, currency) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return '0';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 0
    }).format(number);
  } catch (e) {
    return Math.round(number).toLocaleString('en-US');
  }
};

const formatLabel = (value) => {
  const raw = String(value || '').trim();
  if (!raw || raw.toLowerCase() === '(not set)' || raw.toLowerCase() === 'unknown') {
    return 'Not set';
  }
  return raw;
};

const buildRows = (section) => {
  const rows = Array.isArray(section?.rows) ? section.rows : [];
  const totals = section?.totals || rows.reduce(
    (acc, row) => {
      acc.users += Number(row.users || 0);
      acc.sessions += Number(row.sessions || 0);
      acc.revenue += Number(row.revenue || 0);
      return acc;
    },
    { users: 0, sessions: 0, revenue: 0 }
  );

  return rows
    .map((row) => ({
      label: formatLabel(row.label),
      users: Number(row.users || 0),
      sessions: Number(row.sessions || 0),
      revenue: Number(row.revenue || 0),
      userShare: totals.users > 0 ? (Number(row.users || 0) / totals.users) * 100 : 0
    }))
    .sort((a, b) => b.users - a.users);
};

function AudienceTable({ title, subtitle, rows, totals, currency, emptyText }) {
  return (
    <Card className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="p-6 bg-white border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            {subtitle ? <p className="text-sm text-gray-500 mt-1">{subtitle}</p> : null}
          </div>
          <div className="text-xs text-gray-500">
            {totals?.users ? `${formatNumber(totals.users)} users` : 'No users'}
          </div>
        </div>
      </div>
      <div className="bg-white">
        {rows.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-gray-500">{emptyText}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Segment
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Users
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sessions
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Revenue
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    % Users
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row) => (
                  <tr key={row.label}>
                    <td className="px-4 py-3 text-sm text-gray-900 font-medium">{row.label}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 text-right">{formatNumber(row.users)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 text-right">{formatNumber(row.sessions)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 text-right">
                      {formatCurrency(row.revenue, currency)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 text-right">
                      {row.userShare.toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}

export default function GA4Audience() {
  const { selectedBrand } = useAuth();
  const [dateRange, setDateRange] = useState('last30');
  const [customDates, setCustomDates] = useState({ startDate: '', endDate: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchAudience = async () => {
    if (!selectedBrand?.id) return;
    if (dateRange === 'custom' && (!customDates.startDate || !customDates.endDate)) {
      setError('Please select a start and end date.');
      return;
    }
    try {
      setLoading(true);
      setError('');
      if (selectedBrand.id === DEMO_BRAND_ID) {
        const demo = {
          sections: {
            age: {
              rows: [
                { label: '18-24', users: 12450, sessions: 23310, revenue: 420000 },
                { label: '25-34', users: 21800, sessions: 39200, revenue: 980000 },
                { label: '35-44', users: 16700, sessions: 30150, revenue: 760000 },
                { label: '45-54', users: 9300, sessions: 17600, revenue: 410000 },
                { label: '55-64', users: 5100, sessions: 9200, revenue: 190000 },
                { label: '65+', users: 2200, sessions: 4100, revenue: 85000 }
              ]
            },
            gender: {
              rows: [
                { label: 'female', users: 28700, sessions: 54100, revenue: 1220000 },
                { label: 'male', users: 24400, sessions: 47700, revenue: 980000 },
                { label: 'unknown', users: 3900, sessions: 7400, revenue: 120000 }
              ]
            },
            interests: {
              rows: [
                { label: 'Shoppers/Value Shoppers', users: 18200, sessions: 33100, revenue: 640000 },
                { label: 'Technophiles/Technology News', users: 14600, sessions: 28000, revenue: 520000 },
                { label: 'Food & Dining/Cooking Enthusiasts', users: 11900, sessions: 22100, revenue: 430000 },
                { label: 'Travel/Family Vacations', users: 9400, sessions: 16800, revenue: 360000 },
                { label: 'Sports & Fitness/Fitness Buffs', users: 7800, sessions: 14200, revenue: 290000 },
                { label: 'Beauty & Wellness/Skincare Fans', users: 7200, sessions: 12800, revenue: 310000 }
              ]
            }
          }
        };
        setPayload(demo);
        setLastUpdated(new Date());
        return;
      }

      const url = new URL(`${API_BASE}/api/google-analytics/ga4/audience-demographics`);
      url.searchParams.set('dateRange', dateRange);
      if (dateRange === 'custom') {
        url.searchParams.set('startDate', customDates.startDate);
        url.searchParams.set('endDate', customDates.endDate);
      }
      const res = await fetch(url.toString(), {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        }
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || data?.details || `HTTP ${res.status}`);
      }
      const data = await res.json().catch(() => ({}));
      setPayload(data);
      setLastUpdated(new Date());
    } catch (e) {
      setError(e?.message || 'Failed to load audience data');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAudience();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id, dateRange]);

  const sections = payload?.sections || {};
  const ageRows = useMemo(() => buildRows(sections.age), [sections.age]);
  const genderRows = useMemo(() => buildRows(sections.gender), [sections.gender]);
  const interestRows = useMemo(() => buildRows(sections.interests), [sections.interests]);

  const currency = selectedBrand?.currency;

  const ageTotals = sections?.age?.totals || { users: 0, sessions: 0, revenue: 0 };
  const genderTotals = sections?.gender?.totals || { users: 0, sessions: 0, revenue: 0 };
  const interestTotals = sections?.interests?.totals || { users: 0, sessions: 0, revenue: 0 };

  return (
    <div className="p-6 space-y-6">
      <Card className="rounded-2xl overflow-hidden shadow-xl border border-gray-100">
        <div className="p-6 bg-white text-gray-900 border-b flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
              <img src="/GA4logoicon.svg" alt="GA4" className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl tracking-tight">GA4 Audience Insights</h1>
              <p className="text-sm text-gray-500">
                Demographics and interest audiences from your GA4 property.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="text-sm px-3 py-2 border border-gray-200 rounded-lg bg-white"
            >
              {DATE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {dateRange === 'custom' && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={customDates.startDate}
                  onChange={(e) => setCustomDates((prev) => ({ ...prev, startDate: e.target.value }))}
                  className="text-sm px-3 py-2 border border-gray-200 rounded-lg bg-white"
                />
                <input
                  type="date"
                  value={customDates.endDate}
                  onChange={(e) => setCustomDates((prev) => ({ ...prev, endDate: e.target.value }))}
                  className="text-sm px-3 py-2 border border-gray-200 rounded-lg bg-white"
                />
              </div>
            )}
            <button
              type="button"
              onClick={fetchAudience}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
              disabled={loading}
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <div className="text-xs text-gray-500 whitespace-nowrap">
              {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : 'Not updated yet'}
            </div>
          </div>
        </div>
        {error && (
          <div className="px-6 py-4 bg-red-50 text-sm text-red-600 border-b border-red-100">
            {error}
          </div>
        )}
        {!error && loading && (
          <div className="px-6 py-10 bg-white text-sm text-gray-500 text-center">Loading audience insights...</div>
        )}
        {!loading && !error && payload?.errors && (
          <div className="px-6 py-4 bg-amber-50 text-sm text-amber-700 border-b border-amber-100">
            <div className="font-medium mb-1">Some audience dimensions are unavailable for this property:</div>
            <ul className="list-disc list-inside space-y-0.5 text-xs text-amber-800">
              {Object.entries(payload.errors).map(([section, msg]) => (
                <li key={section}>
                  <span className="font-semibold capitalize">{section}:</span>{' '}
                  <span className="font-mono">{String(msg).slice(0, 220)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-2 text-xs text-amber-600">
              Tip: enable Google Signals in GA4 admin to expose age/gender/interests. If the error mentions <code>invalid_grant</code>, reconnect Google Analytics on the Connections page.
            </div>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AudienceTable
          title="Age Breakdown"
          subtitle="Users by age bracket"
          rows={ageRows}
          totals={ageTotals}
          currency={currency}
          emptyText="No age data available. Make sure Google Signals is enabled."
        />
        <AudienceTable
          title="Gender Breakdown"
          subtitle="Users by gender"
          rows={genderRows}
          totals={genderTotals}
          currency={currency}
          emptyText="No gender data available. Make sure Google Signals is enabled."
        />
      </div>

      <AudienceTable
        title="Interest Categories"
        subtitle="Top interest audiences for this period"
        rows={interestRows}
        totals={interestTotals}
        currency={currency}
        emptyText="No interest data available. Make sure Google Signals is enabled."
      />
    </div>
  );
}
